import {useCallback, useEffect, useRef, useState} from "react";
import {nextFocusKey, rowTabIndex as rowTabIndexOf} from "./utils/keyboardNavigation.ts";
import type {PanelBooking} from "./types.ts";

const TEXT_ENTRY_SELECTOR =
    "textarea, select, a[href], [contenteditable='true'], input:not([type='checkbox']):not([type='radio'])";
const CONTROL_SELECTOR = "button, input[type='checkbox'], input[type='radio']";
const ACTIVATION_KEYS = new Set([" ", "Enter"]);

function isTextEntry(target: HTMLElement): boolean {
    return target.closest(TEXT_ENTRY_SELECTOR) !== null;
}

function isControl(target: HTMLElement): boolean {
    return target.closest(CONTROL_SELECTOR) !== null;
}

function drivesRowCommands(target: HTMLElement, key: string, grid: HTMLElement | null): boolean {
    if (isTextEntry(target)) return false;
    if (!isControl(target)) return true;
    if (grid === null || !grid.contains(target)) return false;
    return !ACTIVATION_KEYS.has(key);
}

interface UseDrillDownKeyboardArgs {
    active: boolean;
    bookings: PanelBooking[];
    expandedKeys: Set<string>;
    onToggleExpand: (key: string) => void;
    onCollapse: (key: string) => void;
    onToggleSelect: (key: string) => void;
    onExtendSelectionTo: (key: string) => void;
    onToggleSelectAll: () => void;
}

interface KeyPress {
    key: string;
    shiftKey: boolean;
    ctrlKey: boolean;
    metaKey: boolean;
    target: HTMLElement;
    preventDefault: () => void;
}

export interface DrillDownKeyboard {
    focusedKey: string | null;
    containerRef: React.RefObject<HTMLDivElement | null>;
    focusRow: (key: string) => void;
    rowTabIndex: (key: string) => 0 | -1;
}

export function useDrillDownKeyboard({
    active,
    bookings,
    expandedKeys,
    onToggleExpand,
    onCollapse,
    onToggleSelect,
    onExtendSelectionTo,
    onToggleSelectAll,
}: UseDrillDownKeyboardArgs): DrillDownKeyboard {
    const [focusedKey, setFocusedKey] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (focusedKey === null) return;
        if (!bookings.some((booking) => booking.key === focusedKey)) setFocusedKey(null);
    }, [bookings, focusedKey]);

    const focusRowElement = useCallback((key: string) => {
        const row = containerRef.current?.querySelector<HTMLElement>(`[data-row-key="${key}"]`);
        row?.focus();
    }, []);

    const moveFocus = useCallback(
        (offset: number, extendSelection: boolean) => {
            const nextKey = nextFocusKey(bookings, focusedKey, offset);
            if (nextKey === null) return;
            setFocusedKey(nextKey);
            focusRowElement(nextKey);
            if (extendSelection) onExtendSelectionTo(nextKey);
        },
        [bookings, focusedKey, focusRowElement, onExtendSelectionTo],
    );

    const handleKey = useCallback(
        ({key, shiftKey, ctrlKey, metaKey, target, preventDefault}: KeyPress) => {
            if (key !== "Escape" && !drivesRowCommands(target, key, containerRef.current)) {
                return;
            }

            switch (key) {
                case "ArrowDown":
                    preventDefault();
                    moveFocus(1, shiftKey);
                    return;
                case "ArrowUp":
                    preventDefault();
                    moveFocus(-1, shiftKey);
                    return;
                case "a":
                case "A":
                    if (!ctrlKey && !metaKey) return;
                    preventDefault();
                    onToggleSelectAll();
                    return;
                case "ArrowRight":
                case "Enter":
                    if (focusedKey === null) return;
                    preventDefault();
                    if (!expandedKeys.has(focusedKey)) onToggleExpand(focusedKey);
                    return;
                case "ArrowLeft":
                    if (focusedKey === null) return;
                    preventDefault();
                    if (expandedKeys.has(focusedKey)) onCollapse(focusedKey);
                    return;
                case "Escape":
                    if (focusedKey === null) return;
                    preventDefault();
                    if (expandedKeys.has(focusedKey)) onCollapse(focusedKey);
                    focusRowElement(focusedKey);
                    return;
                case "x":
                case "X":
                    if (focusedKey === null) return;
                    preventDefault();
                    onToggleSelect(focusedKey);
                    return;
                default:
            }
        },
        [
            expandedKeys,
            focusedKey,
            moveFocus,
            onCollapse,
            onToggleExpand,
            onToggleSelect,
            onToggleSelectAll,
            focusRowElement,
        ],
    );

    useEffect(() => {
        if (!active) return;
        const listener = (event: globalThis.KeyboardEvent) => {
            handleKey({
                key: event.key,
                shiftKey: event.shiftKey,
                ctrlKey: event.ctrlKey,
                metaKey: event.metaKey,
                target: event.target as HTMLElement,
                preventDefault: () => event.preventDefault(),
            });
        };
        document.addEventListener("keydown", listener);
        return () => document.removeEventListener("keydown", listener);
    }, [active, handleKey]);

    const focusRow = useCallback((key: string) => setFocusedKey(key), []);

    const rowTabIndex = useCallback(
        (key: string): 0 | -1 => rowTabIndexOf(bookings, focusedKey, key),
        [bookings, focusedKey],
    );

    return {focusedKey, containerRef, focusRow, rowTabIndex};
}
